/**
 * Everything the exercise page and the exercises row work out from a pile of sets: the kind,
 * lifetime totals, PRs, rep maxes and the day-by-day trend. Warm-ups appear in none of it —
 * the page reads as the exercise's real work, and a warm-up is submaximal by intent.
 */

import { normalizeExerciseName } from '@gymcloud/mixedscript'

import type { LoggedSet, Measurements, RecentExercise } from '~/data/types'
import { dayKey } from '~/lib/dates'
import type { DayValue } from '~/lib/day-value'

/** A logging method: what a set records. Its numbers say which. */
export type ExerciseKind = 'lift' | 'bodyweight' | 'hold' | 'run'

export function kindOf(records: Measurements): ExerciseKind {
  if (records.distance) return 'run'
  if (records.duration) return 'hold'
  if (records.weight) return 'lift'
  return 'bodyweight'
}

/** How a set was logged, which is a question about the set and not about its name. */
export const methodOf = (set: LoggedSet): ExerciseKind => kindOf(set.records)

/** Epley: weight × (1 + reps ÷ 30). A set with no reps counts as one rep. */
export function estimatedOneRepMax(weightKg: number, reps: number | null): number {
  return reps === null ? weightKg : weightKg * (1 + reps / 30)
}

/**
 * The load a set carried, or null when it carried none. Zero is a weight recorded — it is what
 * separates a weighted pull up done with no plates from one logged without weight at all — but
 * it is not a load, so it sets no record and would only drag a graph down to nothing.
 */
const loadOf = (set: LoggedSet): number | null =>
  set.weightKg === null || set.weightKg === 0 ? null : set.weightKg

/** One way an exercise has been logged, with the sets logged that way. */
export interface ExerciseMethod {
  readonly kind: ExerciseKind
  readonly sets: LoggedSet[]
  readonly lastPerformedAt: number
  /** What the latest of them recorded, which is the mark the page wears for the method. */
  readonly records: Measurements
  /** The name the latest of them was typed under, which is what the page calls it. */
  readonly latestName: string
}

/**
 * An exercise's sets split by how they were logged, most recently logged first. Pull ups for
 * reps and pull ups for duration are one exercise logged two ways, and there is no axis that
 * holds both — so each gets its own totals, records and graph rather than one incoherent set.
 */
export function exerciseMethods(sets: readonly LoggedSet[]): ExerciseMethod[] {
  const byKind = new Map<ExerciseKind, LoggedSet[]>()
  for (const set of sets) {
    const held = byKind.get(methodOf(set))
    if (held) {
      held.push(set)
    } else {
      byKind.set(methodOf(set), [set])
    }
  }
  return [...byKind.entries()]
    .map(([kind, kindSets]) => {
      // A group is never empty: it exists because a set fell into it.
      const latest = kindSets.reduce((best, set) =>
        set.performedAt > best.performedAt ? set : best,
      )
      return {
        kind,
        sets: kindSets,
        lastPerformedAt: latest.performedAt,
        records: latest.records,
        latestName: latest.exerciseName,
      }
    })
    .sort((a, b) => b.lastPerformedAt - a.lastPerformedAt)
}

export interface ExerciseTotals {
  readonly setCount: number
  readonly dayCount: number
  readonly firstPerformedAt: number | null
  /** Each set's weight times its reps, summed. */
  readonly weightMovedKg: number
  readonly reps: number
  readonly durationSeconds: number
  readonly distanceMeters: number
}

export function exerciseTotals(sets: readonly LoggedSet[]): ExerciseTotals {
  let firstPerformedAt: number | null = null
  const days = new Set<string>()
  let weightMovedKg = 0
  let reps = 0
  let durationSeconds = 0
  let distanceMeters = 0
  let counted = 0
  for (const set of sets) {
    if (set.isWarmup) {
      continue
    }
    counted += 1
    if (firstPerformedAt === null || set.performedAt < firstPerformedAt) {
      firstPerformedAt = set.performedAt
    }
    days.add(dayKey(set.performedAt))
    if (set.weightKg !== null && set.reps !== null) {
      weightMovedKg += set.weightKg * set.reps
    }
    reps += set.reps ?? 0
    durationSeconds += set.durationSeconds ?? 0
    distanceMeters += set.distanceMeters ?? 0
  }
  return {
    setCount: counted,
    dayCount: days.size,
    firstPerformedAt,
    weightMovedKg,
    reps,
    durationSeconds,
    distanceMeters,
  }
}

export type PRId =
  | 'heaviest'
  | 'one-rep-max'
  | 'bodyweight-ratio'
  | 'most-reps'
  | 'longest-hold'
  | 'longest-run'
  | 'fastest-pace'

export interface ExercisePR {
  readonly id: PRId
  /** The set shown first: filmed footage wins a tie, then the oldest achievement. */
  readonly set: LoggedSet
  /** Every set with the same winning value, front set first. */
  readonly sets: readonly LoggedSet[]
  /**
   * Kilograms, reps, seconds, meters, seconds per kilometer, or a bare multiple of the body's
   * own weight — the id says which.
   */
  readonly value: number
}

/** Seconds per kilometer, or null for a set that did not measure both. */
function paceOf(set: LoggedSet): number | null {
  if (set.durationSeconds === null || set.distanceMeters === null || set.distanceMeters === 0) {
    return null
  }
  return set.durationSeconds / (set.distanceMeters / 1000)
}

/** The best set by a measure, oldest first, so a tie goes to the set that got there first. */
function bestOf(
  sets: readonly LoggedSet[],
  measure: (set: LoggedSet) => number | null,
  better: (candidate: number, best: number) => boolean,
): { sets: LoggedSet[]; value: number } | null {
  let best: { sets: LoggedSet[]; value: number } | null = null
  for (const set of [...sets].sort((a, b) => a.performedAt - b.performedAt)) {
    const value = measure(set)
    if (value !== null && (best === null || better(value, best.value))) {
      best = { sets: [set], value }
    } else if (value !== null && best !== null && value === best.value) {
      best.sets.push(set)
    }
  }
  best?.sets.sort(
    (a, b) => Number(b.hasThumbnail) - Number(a.hasThumbnail) || a.performedAt - b.performedAt,
  )
  return best
}

const higher = (candidate: number, best: number) => candidate > best
const lower = (candidate: number, best: number) => candidate < best

/**
 * The exercise's records by kind, each the set that set it. Warm-ups set nothing.
 *
 * `bodyweightOn` answers what the body weighed around a given set, and is what the ratio
 * record is reckoned against. Left out — or answering null for every set, which is an account
 * that has never stepped on a scale — no ratio record is offered at all.
 */
export function exercisePRs(
  sets: readonly LoggedSet[],
  kind: ExerciseKind,
  bodyweightOn?: (set: LoggedSet) => number | null,
): ExercisePR[] {
  const working = sets.filter((set) => !set.isWarmup)
  const contenders: { id: PRId; found: { sets: LoggedSet[]; value: number } | null }[] = []
  switch (kind) {
    case 'lift':
      contenders.push(
        { id: 'heaviest', found: bestOf(working, loadOf, higher) },
        {
          id: 'one-rep-max',
          found: bestOf(
            working,
            (set) => {
              const load = loadOf(set)
              return load === null ? null : estimatedOneRepMax(load, set.reps)
            },
            higher,
          ),
        },
      )
      if (bodyweightOn) {
        contenders.push({
          id: 'bodyweight-ratio',
          found: bestOf(
            working,
            (set) => {
              const load = loadOf(set)
              const body = bodyweightOn(set)
              // A body with nothing on the scale behind it has nothing to compare against,
              // and the day is left out of the running rather than counted as a zero.
              return load === null || body === null || body <= 0 ? null : load / body
            },
            higher,
          ),
        })
      }
      break
    case 'bodyweight':
      contenders.push({ id: 'most-reps', found: bestOf(working, (set) => set.reps, higher) })
      break
    case 'hold':
      contenders.push({
        id: 'longest-hold',
        found: bestOf(working, (set) => set.durationSeconds, higher),
      })
      break
    case 'run':
      contenders.push(
        { id: 'longest-run', found: bestOf(working, (set) => set.distanceMeters, higher) },
        { id: 'fastest-pace', found: bestOf(working, paceOf, lower) },
      )
      break
  }
  return contenders.flatMap(({ id, found }) =>
    found?.sets[0] ? [{ id, set: found.sets[0], sets: found.sets, value: found.value }] : [],
  )
}

/** A day's best, which is a day and a value with the set that won it hanging off the back. */
export interface TrendPoint extends DayValue {
  /** The actual logged set whose measure won this day. */
  readonly set: LoggedSet
}

/** The measure a day's best is judged by, per kind. */
function trendMeasure(kind: ExerciseKind): (set: LoggedSet) => number | null {
  switch (kind) {
    case 'lift':
      return (set) => {
        const load = loadOf(set)
        return load === null ? null : estimatedOneRepMax(load, set.reps)
      }
    case 'bodyweight':
      return (set) => set.reps
    case 'hold':
      return (set) => set.durationSeconds
    case 'run':
      return (set) => set.distanceMeters
  }
}

/** One point per day the exercise was done, at the day's best, oldest day first. */
export function dailyBest(sets: readonly LoggedSet[], kind: ExerciseKind): TrendPoint[] {
  const measure = trendMeasure(kind)
  const byDay = new Map<string, TrendPoint>()
  for (const set of sets) {
    if (set.isWarmup) {
      continue
    }
    const value = measure(set)
    if (value === null) {
      continue
    }
    const day = dayKey(set.performedAt)
    const best = byDay.get(day)
    if (best === undefined || value > best.value) {
      byDay.set(day, { day, value, set })
    }
  }
  return [...byDay.values()].sort((a, b) => (a.day < b.day ? -1 : 1))
}

export interface RowExercise {
  readonly name: string
  readonly records: Measurements
  readonly lastPerformedAt: number
  readonly thumbnailSetId: string | null
}

/** A set with a picture. It was made on this device, so there is one before anything is up. */
const showsThumbnail = (set: LoggedSet) => set.hasThumbnail

/**
 * The row the timeline actually shows: the server's list, raised wherever this device holds
 * a fresher set — an exercise done offline has to move up too. `sets` newest first, as the
 * data layer keeps them.
 */
export function effectiveRecentExercises(
  server: readonly RecentExercise[],
  sets: readonly LoggedSet[],
): RowExercise[] {
  const byKey = new Map<string, RowExercise>()
  for (const exercise of server) {
    byKey.set(normalizeExerciseName(exercise.name), {
      name: exercise.name,
      records: exercise.records,
      lastPerformedAt: exercise.lastPerformedAt,
      thumbnailSetId: exercise.thumbnailSetId,
    })
  }
  for (const set of sets) {
    const key = normalizeExerciseName(set.exerciseName)
    const known = byKey.get(key)
    if (!known) {
      byKey.set(key, {
        name: set.exerciseName,
        records: set.records,
        lastPerformedAt: set.performedAt,
        thumbnailSetId: showsThumbnail(set) ? set.id : null,
      })
    } else {
      // A fresher set renames the entry and remarks it: the row wears the latest spelling and
      // the latest way it was logged, which is what the server's own answer is built from.
      const fresher = set.performedAt > known.lastPerformedAt
      byKey.set(key, {
        name: fresher ? set.exerciseName : known.name,
        records: fresher ? set.records : known.records,
        lastPerformedAt: Math.max(known.lastPerformedAt, set.performedAt),
        // The server's pick stands; local sets only fill a thumbnail it does not have.
        thumbnailSetId: known.thumbnailSetId ?? (showsThumbnail(set) ? set.id : null),
      })
    }
  }
  return [...byKey.values()].sort(
    (a, b) =>
      b.lastPerformedAt - a.lastPerformedAt ||
      (normalizeExerciseName(a.name) < normalizeExerciseName(b.name) ? -1 : 1),
  )
}
